import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Meta } from "entities/common";
import {
  CreateGatewayRequest,
  GatewayItem,
  GatewayStatistics,
  UpdateGatewayRequest,
} from "entities/gateways";

import { SilentMeta } from "store/silent";

export type UpdateGatewayPayload = {
  id: number;
  body: UpdateGatewayRequest;
};

type GatewayState = {
  items: GatewayItem[];
  current: GatewayItem | null;
  isLoading: boolean;
  isCurrentLoading: boolean;
  meta: Meta;
  statistics: GatewayStatistics;
  isStatisticsLoading: boolean;
  errors: { detail: Record<string, string> } | null;
  successfullyAddedId: number | null;
  successfullyUpdatedId: number | null;
  successfullyRemovedId: number | null;
};

const initialState: GatewayState = {
  items: [],
  current: null,
  isLoading: false,
  isCurrentLoading: false,
  meta: { total: 0, page: 1, page_size: 10, total_pages: 0 },
  statistics: { total: 0, online: 0, offline: 0, never_seen: 0 },
  isStatisticsLoading: false,
  errors: null,
  successfullyAddedId: null,
  successfullyUpdatedId: null,
  successfullyRemovedId: null,
};

const gatewaySlice = createSlice({
  name: "gateways",
  initialState,
  reducers: {
    getGatewayListRequest: {
      reducer(state, action: PayloadAction<string, string, SilentMeta>) {
        void action.payload;
        if (!action.meta?.silent) state.isLoading = true;
      },
      prepare: (query = "", meta: SilentMeta = {}) => ({ payload: query, meta }),
    },
    getGatewayListSuccess: (state, action: PayloadAction<{ items: GatewayItem[] } & Meta>) => {
      state.items = action.payload.items;
      state.meta = {
        total: action.payload.total,
        page: action.payload.page,
        page_size: action.payload.page_size,
        total_pages: action.payload.total_pages,
      };
      state.isLoading = false;
    },
    getGatewayListFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isLoading = false;
    },

    getGatewayRequest: (state, action: PayloadAction<number>) => {
      void action.payload;
      state.isCurrentLoading = true;
    },
    getGatewaySuccess: (state, action: PayloadAction<GatewayItem>) => {
      state.current = action.payload;
      state.isCurrentLoading = false;
    },
    getGatewayFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isCurrentLoading = false;
    },

    createGatewayRequest: (state, action: PayloadAction<CreateGatewayRequest>) => {
      void action.payload;
      state.isLoading = true;
      state.errors = null;
      state.successfullyAddedId = null;
    },
    createGatewaySuccess: (state, action: PayloadAction<GatewayItem>) => {
      state.successfullyAddedId = action.payload.id;
      state.isLoading = false;
    },
    createGatewayFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isLoading = false;
    },

    updateGatewayRequest: (state, action: PayloadAction<UpdateGatewayPayload>) => {
      void action.payload;
      state.isLoading = true;
      state.errors = null;
      state.successfullyUpdatedId = null;
    },
    updateGatewaySuccess: (state, action: PayloadAction<GatewayItem>) => {
      state.current = action.payload;
      state.isLoading = false;
      state.successfullyUpdatedId = action.payload.id;
    },
    updateGatewayFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isLoading = false;
    },

    removeGatewayRequest: (state, action: PayloadAction<number>) => {
      void action.payload;
      state.isLoading = true;
      state.successfullyRemovedId = null;
    },
    removeGatewaySuccess: (state, action: PayloadAction<number>) => {
      state.isLoading = false;
      state.successfullyRemovedId = action.payload;
    },
    removeGatewayFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isLoading = false;
    },

    getGatewayStatisticsRequest: {
      reducer(state, action: PayloadAction<undefined, string, SilentMeta>) {
        void action.payload;
        if (!action.meta?.silent) state.isStatisticsLoading = true;
      },
      prepare: (meta: SilentMeta = {}) => ({ payload: undefined, meta }),
    },
    getGatewayStatisticsSuccess: (state, action: PayloadAction<GatewayStatistics>) => {
      state.statistics = action.payload;
      state.isStatisticsLoading = false;
    },
    getGatewayStatisticsFailure: (state) => {
      state.isStatisticsLoading = false;
    },

    changePage: (state, action: PayloadAction<number>) => {
      state.meta.page = action.payload;
    },
    resetSuccessfullyAddedGateway: (state) => {
      state.successfullyAddedId = null;
    },
    resetSuccessfullyUpdatedGateway: (state) => {
      state.successfullyUpdatedId = null;
    },
    resetSuccessfullyRemovedGateway: (state) => {
      state.successfullyRemovedId = null;
    },
    resetErrorsGateway: (state) => {
      state.errors = null;
    },
    resetGatewayList: (state) => {
      state.items = [];
      state.meta = { total: 0, page: 1, page_size: 10, total_pages: 0 };
    },
  },
});

export const {
  getGatewayListRequest,
  getGatewayListSuccess,
  getGatewayListFailure,
  getGatewayRequest,
  getGatewaySuccess,
  getGatewayFailure,
  createGatewayRequest,
  createGatewaySuccess,
  createGatewayFailure,
  updateGatewayRequest,
  updateGatewaySuccess,
  updateGatewayFailure,
  removeGatewayRequest,
  removeGatewaySuccess,
  removeGatewayFailure,
  getGatewayStatisticsRequest,
  getGatewayStatisticsSuccess,
  getGatewayStatisticsFailure,
  changePage,
  resetSuccessfullyAddedGateway,
  resetSuccessfullyUpdatedGateway,
  resetSuccessfullyRemovedGateway,
  resetErrorsGateway,
  resetGatewayList,
} = gatewaySlice.actions;
export default gatewaySlice.reducer;
